import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { 
  TrendingUp, DollarSign, ShoppingCart, Users, Target,
  ArrowUp, ArrowDown, BarChart3, PieChart, Crown, Medal
} from 'lucide-react';
import AnimatedCounter from '../../components/common/AnimatedCounter';
import { toast } from '../../components/common/Toast';
import { reportsAPI, getApiError } from '../../services/api';

const StatCard = ({ icon: Icon, label, value, sub, color, isDarkMode }) => (
  <motion.div whileHover={{ y: -4 }}
    className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm hover:shadow-xl transition-all`}
  >
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
        <p className={`text-3xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {typeof value === 'number' ? <AnimatedCounter value={Math.round(value * 100) / 100} decimals={typeof value === 'number' && !Number.isInteger(value) ? 2 : 0} /> : value}
        </p>
        {sub && (
          <div className={`flex items-center gap-1 mt-1 text-sm ${sub > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
            {sub > 0 ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
            {Math.abs(sub)}%
          </div>
        )}
      </div>
      <div className={`p-3 rounded-2xl bg-gradient-to-r ${color} shadow-lg shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </motion.div>
);

const Reports = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [overview, setOverview] = useState(null);
  const [salesByType, setSalesByType] = useState([]);
  const [topGestores, setTopGestores] = useState([]);
  const [rankDistribution, setRankDistribution] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [overviewRes, salesRes, topRes, rankRes] = await Promise.all([
          reportsAPI.getOverview(),
          reportsAPI.getSalesByType(),
          reportsAPI.getTopGestores(),
          reportsAPI.getRankDistribution(),
        ]);
        setOverview(overviewRes.data || {});
        setSalesByType(salesRes.data || []);
        setTopGestores(topRes.data || []);
        setRankDistribution(rankRes.data || []);
      } catch (err) {
        toast.error(getApiError(err, 'Error al cargar reportes'));
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const MAX_BAR_WIDTH = 85;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
        <h1 className={`text-3xl font-bold bg-gradient-to-r ${isDarkMode ? 'from-gray-100 to-gray-300' : 'from-gray-900 to-gray-600'} bg-clip-text text-transparent`}>
          {t('common.reports')}
        </h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Reportes y análisis del sistema</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard icon={DollarSign} label="Ingresos Totales" value={overview?.total_revenue || 0} color="from-green-500 to-emerald-500" isDarkMode={isDarkMode} />
        <StatCard icon={Users} label="Gestores Activos" value={overview?.active_gestores || 0} color="from-blue-500 to-cyan-500" isDarkMode={isDarkMode} />
        <StatCard icon={ShoppingCart} label="Total Órdenes" value={overview?.total_orders || 0} color="from-purple-500 to-pink-500" isDarkMode={isDarkMode} />
        <StatCard icon={TrendingUp} label="Comisiones Generadas" value={overview?.total_commissions || 0} color="from-orange-500 to-red-500" isDarkMode={isDarkMode} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 shadow-lg">
              <PieChart className="w-5 h-5 text-white" />
            </div>
            <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Ventas por Tipo</h3>
          </div>
          <div className="space-y-4">
            {salesByType.map((item, i) => {
              const pct = salesByType.reduce((s, x) => s + (x.count || 0), 0) > 0 
                ? ((item.count || 0) / salesByType.reduce((s, x) => s + (x.count || 0), 0)) * 100 : 0;
              return (
                <div key={item.type || i}>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.type || item.category || 'General'}</span>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.count || 0} ({Math.round(pct)}%)</span>
                  </div>
                  <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(pct, MAX_BAR_WIDTH)}%` }}
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                      transition={{ duration: 1, delay: i * 0.1 }}
                    />
                  </div>
                </div>
              );
            })}
            {salesByType.length === 0 && <p className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Sin datos de ventas</p>}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 shadow-lg">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Top Gestores</h3>
          </div>
          <div className="space-y-4">
            {topGestores.map((g, i) => {
              const maxSales = topGestores.length > 0 ? Math.max(...topGestores.map(x => x.total_sales || 0)) : 1;
              const barWidth = maxSales > 0 ? (g.total_sales || 0) / maxSales * 100 : 0;
              return (
                <div key={g.id || i} className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold ${
                    i === 0 ? 'bg-yellow-400 text-yellow-900' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-orange-300 text-orange-900' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                      <span className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{g.name || `Gestor #${g.id}`}</span>
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>${(g.total_sales || 0).toLocaleString()}</span>
                    </div>
                    <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(barWidth, MAX_BAR_WIDTH)}%` }}
                        className="h-full rounded-full bg-gradient-to-r from-orange-500 to-pink-500"
                        transition={{ duration: 1, delay: i * 0.1 }}
                      />
                    </div>
                  </div>
                  <Medal className={`w-4 h-4 ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-400' : i === 2 ? 'text-orange-400' : 'text-transparent'}`} />
                </div>
              );
            })}
            {topGestores.length === 0 && <p className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Sin gestores con ventas</p>}
          </div>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm`}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 shadow-lg">
            <Target className="w-5 h-5 text-white" />
          </div>
          <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Distribución de Rangos</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {rankDistribution.map((r, i) => {
            const maxRank = rankDistribution.length > 0 ? Math.max(...rankDistribution.map(x => x.count || 0)) : 1;
            const barH = maxRank > 0 ? (r.count || 0) / maxRank * 100 : 0;
            return (
              <motion.div key={r.rank || i} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                className={`${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-2xl p-4 text-center`}
              >
                <div className={`w-12 h-12 mx-auto mb-3 rounded-2xl flex items-center justify-center ${
                  i < 2 ? 'bg-gradient-to-br from-orange-500 to-yellow-500' : i < 4 ? 'bg-gradient-to-br from-blue-500 to-cyan-500' : 'bg-gradient-to-br from-gray-500 to-gray-600'
                } shadow-lg`}>
                  <Target className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-gray-900 dark:text-white">{r.rank || r.name || 'Sin rango'}</p>
                <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{r.count || 0}</p>
                <div className={`w-full h-1.5 rounded-full mt-3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                  <motion.div initial={{ width: 0 }} animate={{ width: `${barH}%` }}
                    className="h-full rounded-full bg-gradient-to-r from-orange-500 to-yellow-500"
                    transition={{ duration: 1, delay: i * 0.1 }}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Reports;
